import React from 'react';
import { ShoppingCart, ExternalLink, ShieldCheck, Info } from 'lucide-react';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const image = product.image || product.imageUrl || '';
  const link = product.link || product.affiliateUrl || '#';
  const quantity = product.quantity || (product.units ? `${product.units} un` : '');
  const description = product.description || product.shortDescription || '';

  const handleTrackClick = () => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'click_ver_preco', {
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.line,
        destination_url: link,
        source: 'product_card'
      });

      if (link.toLowerCase().includes('amazon') || link.toLowerCase().includes('amzn')) {
        (window as any).gtag('event', 'click_amazon', {
          item_name: product.name,
          link_url: link,
          location: 'product_card_button'
        });
      }
    }
  };

  return (
    <div className="bg-white/95 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl md:rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden flex flex-col h-full group border border-gray-100 dark:border-gray-700/50 hover:-translate-y-1">
      
      {/* Image Area */}
      <div className="relative p-4 md:p-6 flex items-center justify-center bg-gray-50/50 dark:to-black/30 h-32 md:h-48 overflow-hidden">
        <img 
          src={image} 
          alt={product.name}
          className="max-h-full max-w-full object-contain drop-shadow-lg z-10 transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-2 py-1 md:px-3 md:py-1.5 rounded-full text-[7px] md:text-[10px] font-bold text-store-purple dark:text-yellow-400 shadow-sm flex items-center gap-1 z-20 border border-gray-100">
          <ShieldCheck size={10} className="md:w-3 md:h-3 text-green-500" />
          <span className="tracking-wide uppercase">Verificado</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6 flex-1 flex flex-col relative z-20">
        <div className="mb-1.5 md:mb-2">
           <span className="inline-block px-2 py-0.5 rounded-md bg-store-purple/5 text-store-purple dark:bg-yellow-400/10 dark:text-yellow-300 text-[8px] md:text-[10px] font-bold uppercase tracking-wide border border-store-purple/10">
             {product.line}
           </span>
        </div>
        
        {/* ✅ TÍTULO REDUZIDO: de text-xl para text-lg */}
        <h3 className="font-semibold text-gray-800 dark:text-white text-xs md:text-base leading-tight mb-2 flex-grow line-clamp-2">
          {product.name}
        </h3>
        
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[9px] md:text-xs text-gray-400 font-semibold uppercase tracking-wide">📦 {quantity}</span>
        </div>

        <div className="space-y-3 mt-auto">
          {/* ✅ BOTÃO REDUZIDO: de text-lg para text-sm */}
          <a 
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleTrackClick}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-br from-store-purple to-store-dark hover:from-store-dark hover:to-store-purple text-white py-2.5 md:py-3 rounded-lg md:rounded-xl font-bold text-xs md:text-sm shadow-lg shadow-purple-500/10 hover:shadow-purple-500/20 transition-all active:scale-95"
          >
            <ShoppingCart size={16} className="hidden md:block" />
            VER PREÇO
          </a>
          
          <div className="flex flex-col gap-1 items-center">
            <span className="text-[7px] md:text-[9px] text-gray-400 font-semibold flex items-center gap-1 opacity-70">
              <ExternalLink size={8} /> Link Seguro Amazon
            </span>
            <p className="text-[6px] md:text-[8px] text-gray-300 text-center leading-none italic px-1">
              Redirecionamento seguro para a Amazon.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;